import React, { useState, useEffect } from "react";
import axios from "axios";
import CustomButton from "../../pages/CustomButton";
import { getAuthHeaders } from "../../services/useAuth";
import { BASE_URL } from "../../config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const SchemeProduct = () => {
  const [viewMode, setViewMode] = useState("view");
  const [filterDivision, setFilterDivision] = useState("");
  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectAll, setSelectAll] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceListData, setPriceListData] = useState([]);
  const [priceHistoryData, setPriceHistoryData] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Fetch states and divisions
  useEffect(() => {
    fetchAllStates();
    fetchAllDivisions();
  }, []);

  const fetchAllStates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/all-state`, {
        headers: getAuthHeaders(),
      });
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
    } finally {
      setLoading(false);
    }
  };

  // Handle select all states
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedStates([...states]);
    } else {
      setSelectedStates([]);
    }
  };

  // Handle individual state selection
  const handleStateSelection = (state, checked) => {
    if (checked) {
      setSelectedStates([...selectedStates, state]);
    } else {
      setSelectedStates(selectedStates.filter((s) => s.id !== state.id));
      setSelectAll(false);
    }
  };

  // Check if a state is selected
  const isStateSelected = (state) => {
    return selectedStates.some((s) => s.id === state.id);
  };

  // Handle status toggle for product scheme
  const handleStatusToggle = async (productId, isActive) => {
    try {
      setLoading(true);
      
      // Call API to toggle product scheme status
      await axios.post(
        `${BASE_URL}/api/product/scheme-status`,
        {
          product_id: productId,
          is_active: !isActive
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Product scheme ${!isActive ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the data
      if (filterDivision) {
        handleApplyFilters();
      }
    } catch (error) {
      console.error("Error toggling product scheme status:", error);
      toast.error("Failed to update product scheme status");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDivisions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/divisions`, {
        headers: getAuthHeaders(),
      });
      setDivisions(response.data);
      if (response.data.length > 0 && !filterDivision) {
        setFilterDivision(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching divisions:", error);
      toast.error("Failed to fetch divisions");
    } finally {
      setLoading(false);
    }
  };

  // Fetch products for Add Price
  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/api/product/?division_id=${filterDivision}&start_date=${filterDate}`,
        { headers: getAuthHeaders() }
      );

      const products = Array.isArray(response.data) ? response.data : [];

      const transformedProducts = products.map((product) => {
        const price = product.prices?.find(
          (p) => p.division_id === parseInt(filterDivision)
        );
        return {
          id: product.id,
          code: product.mapping_code || product.id.toString(),
          name: product.name,
          pack: product.packaging,
          mrp: price?.mrp || "",
          ptd: price?.ptd || "",
          pts: price?.s_rate || "",
          ptr: price?.r_rate || "",
          gst_rate: price?.gst_rate || "",
          c_slot: price?.c_slot || "",
          is_active: product.scheme_active || false // Add scheme status
        };
      });

      setPriceListData(transformedProducts);
      toast.success("Products loaded successfully");
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterDivision) {
      handleApplyFilters();
    }
  }, [filterDivision]);

  // Update row values
  const handleEditPriceListItem = (e, field, itemId) => {
    const updatedData = priceListData.map((item) =>
      item.id === itemId ? { ...item, [field]: e.target.value } : item
    );
    setPriceListData(updatedData);
  };

  // Submit (Add / Update)
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const stateIds = selectedStates.map((s) => s.id);

      if (editingPriceId) {
        // UPDATE
        const payload = {
          product_id: editingPriceId,
          division_id: parseInt(filterDivision),
          state_ids: stateIds,
          effective_from: effectiveDate,
          mrp: parseFloat(priceListData[0]?.mrp || 0),
          ptd: parseFloat(priceListData[0]?.ptd || 0),
          s_rate: parseFloat(priceListData[0]?.pts || 0),
          r_rate: parseFloat(priceListData[0]?.ptr || 0),
          gst_rate: parseFloat(priceListData[0]?.gst_rate || 0),
          c_slot: parseInt(priceListData[0]?.c_slot || 0),
        };

        await axios.put(
          `${BASE_URL}/api/product/product-prices-update-for-states`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success("Price updated successfully");
      } else {
        // ADD - Create scheme for products
        const payload = {
          product_ids: priceListData.map((p) => p.id),
          division_id: parseInt(filterDivision),
          state_ids: stateIds,
          start_date: filterDate,
        };

        await axios.post(`${BASE_URL}/api/product/create-scheme`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Scheme created successfully");
      }

      // Reset
      setEditingPriceId(null);
      setSearchParams({});
      setPriceListData([]);
      setSelectedStates([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error submitting scheme:", error);
      toast.error("Failed to submit scheme");
    } finally {
      setLoading(false);
    }
  };

  // Fetch history for Edit tab
  const fetchPriceHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/api/product/?division_id=${filterDivision}&isactive=true`,
        { headers: getAuthHeaders() }
      );

      const products = Array.isArray(response.data) ? response.data : [];

      const historyData = products.flatMap((product) =>
        (product.prices || [])
          .filter((price) => price.division_id === parseInt(filterDivision))
          .map((price) => ({
            division_id: filterDivision,
            division_name:
              divisions.find((d) => d.id === parseInt(filterDivision))?.name ||
              `Division ${filterDivision}`,
            state_names: Array.isArray(product.state_names)
              ? product.state_names
              : [],
            effective_from: price.effective_from || "",
            effective_to: price.effective_to || "",
            product_id: product.id,
            is_active: product.scheme_active || false
          }))
      );

      const uniqueHistoryData = historyData.filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.division_id === item.division_id &&
              t.effective_from === item.effective_from &&
              t.effective_to === item.effective_to
          )
      );

      setPriceHistoryData(uniqueHistoryData);
    } catch (error) {
      console.error("Error fetching price history:", error);
      toast.error("Failed to fetch price history");
    } finally {
      setLoading(false);
    }
  };

  // Autofill for View/Edit
  const handleViewEdit = async (divisionId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/api/product/?division_id=${divisionId}`,
        { headers: getAuthHeaders() }
      );

      const products = Array.isArray(response.data) ? response.data : [];
      if (products.length > 0) {
        const firstPrice = products[0].prices?.find(
          (p) => p.division_id === parseInt(divisionId)
        );

        // ✅ Set effective date
        if (firstPrice) {
          setEffectiveDate(firstPrice.effective_from || filterDate);
        }

        // ✅ Transform product rows
        const transformedProducts = products.map((product) => {
          const price = product.prices?.find(
            (p) => p.division_id === parseInt(divisionId)
          );
          return {
            id: product.id,
            code: product.mapping_code || product.id.toString(),
            name: product.name,
            pack: product.packaging,
            mrp: price?.mrp || "",
            ptd: price?.ptd || "",
            pts: price?.s_rate || "",
            ptr: price?.r_rate || "",
            gst_rate: price?.gst_rate || "",
            c_slot: price?.c_slot || "",
            is_active: product.scheme_active || false
          };
        });
        setPriceListData(transformedProducts);

        // ✅ Autofill states (match by name since API gives state_names)
        const assignedStateNames = products[0]?.state_names || [];
        const matchedStates = states.filter((s) =>
          assignedStateNames.includes(s.name)
        );
        setSelectedStates(matchedStates);
        setSelectAll(matchedStates.length === states.length);

        setFilterDivision(divisionId);
        setViewMode("view");
        setEditingPriceId(products[0].id);
        toast.success("Product data loaded for editing");
      }
    } catch (error) {
      console.error("Error loading product data:", error);
      toast.error("Failed to load product data");
    } finally {
      setLoading(false);
    }
  };

  // Cancel
  const handleCancel = () => {
    setSelectAll(false);
    setSelectedStates([]);
    setEditingPriceId(null);
    setSearchParams({});
    setPriceListData([]);
  };

  useEffect(() => {
    if (viewMode === "edit" && filterDivision) {
      fetchPriceHistory();
    }
  }, [viewMode, filterDivision]);

  return (
    <div className=" flex flex-col bg-gray-50">
      <main className="flex-grow container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">
          {viewMode === "view" ? "Product Scheme" : "Edit Product Scheme"}
          {editingPriceId && " (Editing)"}
        </h1>

        {loading && (
          <div className="mb-4 p-3 bg-blue-100 text-blue-700 rounded-md">
            Loading...
          </div>
        )}

        <div className="flex mb-6 border-b border-gray-300">
          <button
            className={`py-3 px-6 rounded-none rounded-t-lg font-semibold text-sm transition-all duration-200 ${
              viewMode === "view"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-b-2 border-blue-500 shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-b-2 border-transparent"
            }`}
            onClick={() => {
              setViewMode("view");
              setEditingPriceId(null);
              setPriceListData([]);
            }}
          >
            Add Scheme
          </button>
          <button
            className={`py-3 px-6 rounded-none rounded-t-lg font-semibold text-sm transition-all duration-200 ${
              viewMode === "edit"
                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-b-2 border-blue-500 shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-b-2 border-transparent"
            }`}
            onClick={() => {
              setViewMode("edit");
              setEditingPriceId(null);
              setPriceListData([]);
            }}
          >
            Edit Scheme
          </button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md mb-6 border border-gray-300">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded"
                value={filterDivision}
                onChange={(e) => setFilterDivision(e.target.value)}
                disabled={loading}
              >
                <option value="">Select Division</option>
                {divisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
            </div>
            {viewMode === "view" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  className="w-full p-2 border border-gray-300 rounded"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}
            <div className="flex items-end">
              <CustomButton
                variant="save"
                onClick={
                  viewMode === "view" ? handleApplyFilters : fetchPriceHistory
                }
                disabled={loading || !filterDivision}
              >
                Go
              </CustomButton>
            </div>
          </div>
        </div>

        {viewMode === "view" ? (
          <>
            <p className="text-sm text-gray-600 mb-6">
              Note: Set FromDate since when prices are applicable from and Then
              Price changing date
            </p>

            {priceListData.length > 0 ? (
              <>
                <div className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-300 mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-blue-600">
                        <tr>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white border border-gray-300">
                            S.No
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white border border-gray-300">
                            Product Code
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white border border-gray-300">
                            Product
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white border border-gray-300">
                            Packaging
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white border border-gray-300">
                            Scheme Status
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {priceListData.map((item, index) => (
                          <tr
                            key={item.id || index}
                            className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                          >
                            {/* S.No */}
                            <td className="px-4 py-3 border border-gray-300 text-center text-sm font-medium text-gray-900">
                              {index + 1}
                            </td>

                            {/* Product Code */}
                            <td className="px-4 py-3 border border-gray-300 text-center text-sm font-medium text-gray-900">
                              {item.code}
                            </td>

                            {/* Product */}
                            <td className="px-4 py-3 border border-gray-300 text-center text-sm font-medium text-gray-900">
                              {item.name}
                            </td>

                            {/* Packaging */}
                            <td className="px-4 py-3 border border-gray-300 text-center text-sm font-medium text-gray-900">
                              {item.pack}
                            </td>

                            {/* Scheme Status - Toggle */}
                            <td className="px-4 py-3 border border-gray-300 text-center">
                              <label className="inline-flex items-center me-5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={item.is_active || false}
                                  onChange={() => handleStatusToggle(item.id, item.is_active)}
                                  disabled={loading}
                                />
                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-green-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                <span className="ml-2 text-sm font-medium text-gray-900">
                                  {item.is_active ? "Active" : "Inactive"}
                                </span>
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-lg mt-6 border-2 border-gray-200">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b border-gray-200 pb-2">
                    Select States
                  </h3>

                  <div className="mb-4">
                    <label className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        disabled={loading}
                      />
                      <span className="text-sm font-semibold text-gray-700">
                        Select All States
                      </span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                    {states.map((state, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-white transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isStateSelected(state)}
                          onChange={(e) =>
                            handleStateSelection(state, e.target.checked)
                          }
                          className="w-4 h-4 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          disabled={loading}
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          {state.name}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="flex space-x-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSubmit}
                      disabled={loading || selectedStates.length === 0}
                      className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editingPriceId ? "Update" : "Submit"}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={loading}
                      className="px-8 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg shadow-md hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <p className="text-gray-500">
                  No products found. Select a division and click "Go" to load
                  products.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {priceHistoryData.length > 0 ? (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gradient-to-r from-blue-600 to-blue-700">
                      <tr>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          S.No
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Division
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          States
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Start Date
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          End Date
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Status
                        </th>
                        <th className="px-4 py-4 text-center text-sm font-semibold text-white border border-gray-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {priceHistoryData.map((item, index) => (
                        <tr
                          key={item.division_id || index}
                          className={
                            index % 2 === 0
                              ? "bg-white hover:bg-gray-50"
                              : "bg-gray-50 hover:bg-gray-100"
                          }
                        >
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.division_name}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.state_names?.length > 0
                              ? item.state_names.join(", ")
                              : "-"}
                          </td>

                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.effective_from}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            {item.effective_to}
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center text-sm font-medium text-gray-900">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              item.is_active 
                                ? "bg-green-100 text-green-800" 
                                : "bg-red-100 text-red-800"
                            }`}>
                              {item.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-4 py-4 border border-gray-300 text-center">
                            <div className="flex justify-center space-x-3">
                              <CustomButton
                                variant="save"
                                className="text-xs font-semibold rounded-md shadow-sm disabled:opacity-50"
                                onClick={() => handleViewEdit(item.division_id)}
                                disabled={loading}
                              >
                                View/Edit
                              </CustomButton>
                              <CustomButton
                                variant="delete"
                                className="text-xs font-semibold rounded-md shadow-sm disabled:opacity-50"
                                onClick={() => handleStatusToggle(item.product_id, item.is_active)}
                                disabled={loading}
                              >
                                {item.is_active ? "Deactivate" : "Activate"}
                              </CustomButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <p className="text-gray-500">
                  No scheme history found. Select a division and click "Go" to
                  load scheme history.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default SchemeProduct;